import React, { useEffect, useRef, useState } from 'react';
import { Checkbox, Input, Select } from '@/shared/components';
import { useDraftValue } from '@/shared/hooks/useDraftValue';
import { dataTypeOf, extractAllowedOptions, humanizeKey, splitCsv } from './configValue';
import type { ConfigSettingLike } from './types';

const BOOLEAN_OPTIONS = [
    { value: 'true', label: 'Yes' },
    { value: 'false', label: 'No' },
];

// Slot a table registers this cell's error setter into. Save errors are pushed
// through it so only the cell re-renders: an error held in the table's own state
// would sit in its column memo deps and remount the editor the operator is typing in.
export type ConfigEditErrorSink = React.MutableRefObject<((error: string | null) => void) | null>;

interface Props {
    setting: ConfigSettingLike;
    draftRef: React.MutableRefObject<string>;
    errorSinkRef: ConfigEditErrorSink;
}

// Module-level component: its type identity never changes across renders of the
// owning table, so typing here re-renders only this cell and never remounts it
// (the focus-loss bug this module exists to fix).
export const ConfigValueEditCell: React.FC<Props> = ({ setting, draftRef, errorSinkRef }) => {
    const [value, setValue] = useDraftValue(draftRef);
    const [error, setError] = useState<string | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const dataType = dataTypeOf(setting);
    const fieldName = setting.configOption?.label ?? humanizeKey(setting.key);
    const errorId = `config-edit-error-${setting.id}`;
    const describedBy = error ? errorId : undefined;

    useEffect(() => {
        errorSinkRef.current = setError;
        return () => {
            errorSinkRef.current = null;
        };
    }, [errorSinkRef]);

    // Activating Edit unmounts the focused Edit button, so move focus onto the
    // editor or the keyboard user is dropped back to the document body.
    useEffect(() => {
        containerRef.current?.querySelector<HTMLElement>('input, [role="combobox"]')?.focus();
    }, []);

    let widget: React.ReactNode;
    if (dataType === 'multiselect') {
        const allowed = extractAllowedOptions(setting.configOption);
        if (allowed.length === 0) {
            // No declared allowed set: fall back to a free-form comma-separated input.
            widget = (
                <Input
                    value={value}
                    aria-label={fieldName}
                    aria-describedby={describedBy}
                    aria-invalid={!!error}
                    onChange={(e) => setValue(e.target.value)}
                />
            );
        } else {
            const selected = splitCsv(value);
            widget = (
                <div
                    role="group"
                    aria-label={fieldName}
                    aria-describedby={describedBy}
                    className="flex flex-col gap-2"
                >
                    {allowed.map((opt) => (
                        <Checkbox
                            key={opt.value}
                            id={`edit-${setting.id}-${opt.value}`}
                            label={opt.label}
                            ariaLabel={opt.label}
                            checked={selected.includes(opt.value)}
                            onChange={(checked) => {
                                const next = checked
                                    ? [...selected, opt.value]
                                    : selected.filter((x) => x !== opt.value);
                                // Serialise in the option's declared order for stable payloads.
                                setValue(allowed.filter((a) => next.includes(a.value)).map((a) => a.value).join(','));
                            }}
                        />
                    ))}
                </div>
            );
        }
    } else if (dataType === 'boolean') {
        widget = (
            <Select
                size="sm"
                options={BOOLEAN_OPTIONS}
                ariaLabel={fieldName}
                ariaDescribedBy={describedBy}
                error={!!error}
                value={value}
                onChange={setValue}
            />
        );
    } else if (dataType === 'integer') {
        widget = (
            <Input
                value={value}
                aria-label={fieldName}
                aria-describedby={describedBy}
                aria-invalid={!!error}
                onChange={(e) => setValue(e.target.value)}
                type="number"
                step={1}
            />
        );
    } else if (dataType === 'decimal' || dataType === 'money') {
        widget = (
            <Input
                value={value}
                aria-label={fieldName}
                aria-describedby={describedBy}
                aria-invalid={!!error}
                onChange={(e) => setValue(e.target.value)}
                type="number"
                step={0.01}
                min="0"
            />
        );
    } else {
        widget = (
            <Input
                value={value}
                aria-label={fieldName}
                aria-describedby={describedBy}
                aria-invalid={!!error}
                onChange={(e) => setValue(e.target.value)}
            />
        );
    }

    return (
        <div ref={containerRef}>
            {widget}
            {error && (
                <p id={errorId} role="alert" className="text-mp-error text-xs mt-1">{error}</p>
            )}
        </div>
    );
};
